"use client";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import { motion } from "framer-motion";
import { useRef } from "react";
import { TESTIMONIALS } from "~/lib/testimonials";

import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";
import { lighten, useTheme } from "@mui/material/styles";

type Testimonial = (typeof TESTIMONIALS)[number];

// Per-card styling: rotation (deg) and vertical offset (px) for the stacked look.
const CARD_STYLES: Array<{ rotate: number; y: number; x: number }> = [
  { rotate: -5, y: 14, x: -10 },
  { rotate: 2.5, y: 6, x: 6 },
  { rotate: -1, y: 0, x: 0 },
];

type CardColorKey = "primary" | "secondary" | "success";
const CARD_COLOR_KEYS: CardColorKey[] = ["primary", "secondary", "success"];

function resolveCardColor(theme: Theme, key: CardColorKey): string {
  return lighten(theme.palette[key].main, 0.93);
}

export default function TestimonialCard({
  testimonial,
  cardIndex,
  totalCards,
}: {
  testimonial: Testimonial;
  cardIndex: number;
  totalCards: number;
}) {
  const theme = useTheme();
  const style = CARD_STYLES[cardIndex % CARD_STYLES.length];

  // Soft pastel background — 10 % opacity of the accent color
  const colorKey = CARD_COLOR_KEYS[cardIndex % CARD_COLOR_KEYS.length];
  // Use solid card background so testimonials are not see-through
  const bgColor = `color-mix(in srgb, var(--mui-palette-${colorKey}-main), white 93%)`;
  const borderColor = `color-mix(in srgb, var(--mui-palette-${colorKey}-main), transparent 80%)`;
  const textColor = `var(--mui-palette-${colorKey}-dark)`;

  const ref = useRef<HTMLDivElement | null>(null);

  const handleClick = () => {
    ref.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
    ref.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <motion.div
      initial={{
        rotate: style.rotate,
        y: style.y,
        x: style.x,
        scale: 1,
        zIndex: totalCards - cardIndex,
      }}
      whileHover={{
        rotate: 0,
        y: -12,
        x: 0,
        scale: 1.08,
        zIndex: 20,
        boxShadow: theme.shadows[6],
      }}
      whileFocus={{
        rotate: 0,
        y: -12,
        x: 0,
        scale: 1.08,
        zIndex: 20,
        boxShadow: theme.shadows[6],
      }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
      ref={ref}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      style={{
        cursor: "pointer",
        outline: "none",
        borderRadius: `${(theme.shape.borderRadius as number) * 3}px`,
        padding: "24px 28px",
        backgroundColor: bgColor,
        border: `1.5px solid ${borderColor}`,
        boxShadow: "none",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 160,
        flex: "0 0 80%",
        position: "relative",
        marginBottom: 12,
      }}
    >
      <FormatQuoteIcon
        sx={{
          color: `${colorKey}.main`,
          fontSize: 32,
          opacity: 0.6,
          alignSelf: "flex-start",
          transform: "scaleX(-1)",
        }}
      />
      <Typography
        sx={{
          fontStyle: "italic",
          fontFamily: "var(--font-headline)",
          fontSize: { xs: "1rem", md: "1.05rem" },
          lineHeight: 1.55,
          flexGrow: 1,
          color: textColor,
        }}
      >
        {testimonial.quote}
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          textAlign: "right",
          color: `color-mix(in srgb, ${textColor}, transparent 25%)`,
        }}
      >
        — {testimonial.author}
      </Typography>
    </motion.div>
  );
}
